import React, { useEffect, useState, useMemo } from "react";
import useLayoutStore from "@/stores/layout/layoutStore";
import DToastContainer from "@/components/DToast/DToastContainer";
import SlidingChatSidebar from "@/components/Global/SlidingChatSidebar";
import AiChatbotIcon from '@/shared/icons/AiChatbotIcon';
import { useUserStore } from "@/stores/user/userStore";
import GlobalModals from "@/components/GlobalModals";
import { useConversationStore } from "@/hooks/useConversationStore";
import { getBaseUrl } from '@/utils/getBaseUrl';
import BurgerMenuIcon from '@/shared/icons/BurgerMenuIcon';
import CloseIcon from '@/shared/icons/CloseIcon';
import IconWrapper from "@/shared/components/UI/IconWrapper";
import MobileNavDrawer from "@/v3-dashboard/components/MobileNavDrawer";
import ReferralProgram from "@/components/ReferralProgram";
import DNotificationHeader from "@/components/DNotificationHeader";
import HeaderAccountMenu from "@/v3-dashboard/components/HeaderAccountMenu";
import HeaderLearningHub from "@/v3-dashboard/components/HeaderLearningHub/HeaderLearningHub";
import FeedbackRecordingModal from '@/components/FeedbackRecordingModal';
import FeedbackRecordingButton from '@/components/FeedbackRecordingButton';
import { useSessionRecording } from '@/hooks/useSessionRecording';

/**
 * This layout component is used to display the content of the children
 * when the device screen size is smaller than the `sm` breakpoint.
 */
const LayoutMobile = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const setSidebarOpen = useLayoutStore((state) => state.setSidebarOpen);
  const userData = useUserStore((state) => state.user);
  const isFeedbackEnabled = true;
  const { getRecordingEvents } = useSessionRecording({ enabled: isFeedbackEnabled });
  // Use the conversation store for the iframe embed (kb_id for the embedded chatbot)
  const KB_ID = 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a';
  const { currentConversation, setCurrentConversation } = useConversationStore(KB_ID, 'embed');

  // Track the initial conversation ID when chat opens (to prevent iframe reload during active session)
  const initialConversationIdRef = React.useRef(currentConversation?.id);

  // State to force iframe reload on reset (timestamp ensures unique URL)
  const [resetTimestamp, setResetTimestamp] = React.useState(Date.now());

  // Track the previous open state to detect when chat is opened/reopened
  const prevChatOpenRef = React.useRef(isChatOpen && !isChatMinimized);

  // Update the initial conversation ID ref when chat is about to open
  // This runs BEFORE render, ensuring the ref is set before iframe URL is calculated
  const isCurrentlyOpen = isChatOpen && !isChatMinimized;
  const wasPreviouslyClosed = !prevChatOpenRef.current;

  if (isCurrentlyOpen && wasPreviouslyClosed) {
    initialConversationIdRef.current = currentConversation?.id;
  }

  // Update the previous state after render
  React.useEffect(() => {
    prevChatOpenRef.current = isCurrentlyOpen;
  }, [isCurrentlyOpen]);

  // Listen for conversation creation events from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Handle conversation creation from iframe
      if (event.data.eventType === 'conversationCreated' && event.data.kbId === KB_ID) {
        // Update the store so it's available for next time chat opens
        setCurrentConversation({ id: event.data.conversationId });
        // DO NOT update initialConversationIdRef here - this would cause iframe reload
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [KB_ID, setCurrentConversation]);

  // Memoize the iframe src URL to prevent unnecessary re-renders
  // Use initialConversationIdRef to prevent iframe reload when conversation is created during active session
  const iframeSrc = useMemo(() => {
    const baseUrl = `${getBaseUrl()}embed/`;
    const params = new URLSearchParams({
      kb_id: 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a',
      token: '5e5ff150-8321-43cc-b0ed-5f89b44ecf81',
      modeltype: 'gpt-4-omnimodel-mini',
      tabs: 'false',
      client_full_name: userData?.full_name || '',
      client_email: userData?.email || ''
    });

  
 
    if (initialConversationIdRef.current) {
      params.append('conversation_id', initialConversationIdRef.current);
    }

    // Add reset timestamp to force iframe reload when reset is clicked
    // This ensures the URL changes and iframe reloads with fresh state
    params.append('_reset', resetTimestamp.toString());

    return `${baseUrl}?${params.toString()}`;
  }, [userData?.full_name, userData?.email, resetTimestamp, isChatOpen, isChatMinimized]);

  const handleToggleNav = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setSidebarOpen(true);
  }, []);

  return (
    <>
    <main className='flex flex-col w-full max-w-full overflow-hidden h-full'>
      {/* TopBar - always visible */}
      <header className="flex justify-between items-center  py-[12px] pr-v3-sm pl-v3-sm sm:pl-[28px] h-[60px] bg-v3-glass backdrop-blur-[10px]">
        <DToastContainer hideInMobile />

        {/* Left side - Burger/Close menu */}
        <div className="flex items-center gap-v3-sm">
          <button
            onClick={handleToggleNav}
            className="flex items-center justify-center  hover:bg-grey-2 rounded-v3-sm transition-colors"
            aria-label="Toggle navigation menu"
          >
            {isOpen ? (
              <IconWrapper size={20}>
                <CloseIcon className="w-full h-full text-v3-black-max" />
              </IconWrapper>
            ) : (
             
              <IconWrapper size={32}>
                <BurgerMenuIcon className=" text-v3-black-max" />
              </IconWrapper>
             
            )}
          </button>
        </div>

        {/* Right side - Referral, Notifications, Learning Hub, and Account menu */}
        <div className="flex items-center  sm:pr-v3-sm flex-shrink-0">
          <div className="flex items-center flex-shrink-0 gap-v3-xs">
            <ReferralProgram />
            {isFeedbackEnabled && <FeedbackRecordingButton getRecordingEvents={getRecordingEvents} />}
            <HeaderLearningHub />
            <DNotificationHeader />
            
             <HeaderAccountMenu closeMobileNav={handleToggleNav} />
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer - shown when burger menu is open */}
      <MobileNavDrawer isOpen={isOpen} onClose={() => setIsOpen(false)} />

      {/* Main content - hidden when burger menu is open */}
      {!isOpen && (
        <div className="flex flex-col grow overflow-hidden">
          {children}
        </div>
      )}

      {isChatOpen && !isChatMinimized && (
        <SlidingChatSidebar
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          onMinimize={() => setIsChatMinimized(true)}
          onReset={() => {
            // Clear the initial conversation ID ref when reset is clicked
            initialConversationIdRef.current = null;
            // Update reset timestamp to force iframe URL change and reload
            setResetTimestamp(Date.now());
          }}
        >
          <div className="h-full w-full">
            <iframe src={iframeSrc} allow="clipboard-write; clipboard-read; ;microphone" width="100%" height="100%" style={{border: 0}}></iframe>
          </div>
        </SlidingChatSidebar>
      )}

      {isChatOpen && isChatMinimized && (
        <div className="fixed bottom-6 right-6 z-50 bg-white shadow-lg rounded-full flex items-center px-4 py-2 border border-grey-10 cursor-pointer min-w-[120px] min-h-[48px]" onClick={() => setIsChatMinimized(false)}>
          <AiChatbotIcon className="w-4 h-4 text-purple-300 mr-2 mt-0.5" />
          <span className="font-medium text-grey-75 text-sm">Chat with us</span>
        </div>
      )}

      {/* Feedback Recording Modal */}
      <FeedbackRecordingModal />

      {/* Global Modals - includes Claude Models Modal */}
      <GlobalModals />
    </main>
    </>
  );
};

export default LayoutMobile;
